import React, { useState, useEffect } from 'react';
import { 
    Paper,
    TablePagination,
    Typography,
    TextField,
    Box,
    Button,
    LinearProgress,
    InputAdornment,
    Chip,
    Stack
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ResponsiveTable from '../../common/ResponsiveTable';
import { reportService } from '../../../services/api';

const CourseEngagementReport = () => {
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [data, setData] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);    
    
    const theme = useTheme();
    const [filters, setFilters] = useState({
        search: '',
    });
    const [searchTimeout, setSearchTimeout] = useState(null);

    const columns = [
        { id: 'coursename', label: 'Course Name', minWidth: 200 },
        { 
            id: 'learnersenrolled', 
            label: 'Learners Enrolled', 
            minWidth: 150,
            format: (row) => (               
                <Typography variant="body2" sx={{ minWidth: '35px' }}>
                    {row.learnersenrolled}
                </Typography>
            )
        },
        { id: 'avgcompletion', label: 'Avg. Completion %', minWidth: 160 },
        { id: 'mostSkippedModule', label: 'Most Skipped Modules', minWidth: 250 }
    ];
    

    const fetchData = async () => {
        setLoading(true);
        try {
            const response = await reportService.getCourseEngagement({
                page,
                limit: rowsPerPage,
                search: filters.search
            });
            
            if (response.data.success) {
                setData(response.data.data);
                setTotal(response.data.total);
            }
        } catch (error) {
            console.error('Error fetching course engagement report:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [page, rowsPerPage]);

    useEffect(() => {
        if (searchTimeout) {
            clearTimeout(searchTimeout);
        }

        const timeout = setTimeout(() => {
            setPage(0);
            fetchData();
        }, 500);

        setSearchTimeout(timeout);

        return () => clearTimeout(timeout);
    }, [filters.search]);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleSearchChange = (event) => {
        setFilters(prev => ({
            ...prev,
            search: event.target.value
        }));
    };

    const handleExport = async () => {
        try {
            const response = await reportService.exportCourseEngagement({
                search: filters.search
            });
            
            if (response) {
                const blob = new Blob([response.data], { type: 'text/csv' });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.style.display = 'none';
                a.href = url;
                a.download = `course-engagement-report-${new Date().toISOString().split('T')[0]}.csv`;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
            }
        } catch (error) {
            console.error('Error exporting course engagement report:', error);
        }
    };

    const formatPercentage = (value) => {
        return (
            <Box display="flex" alignItems="center" gap={1}>
                <Box>
                    <LinearProgress 
                        variant="determinate" 
                        value={Math.min(value, 100)}
                    />
                </Box>
                <Typography variant="body2" sx={{ minWidth: '35px' }}>
                    {value}%
                </Typography>
            </Box>
        );
    };

    const formatSkippedModules = (value) => {
        let topModules = value ? value.split('| ') : [];
        return (            
            <Stack direction="column" spacing={1}> 
                {topModules.map((module, index) => (
                    <Chip 
                        key={index}
                        label={module}
                        title={module}                       
                        size="small"
                        sx={{
                            maxWidth: 300,
                            width: 'fit-content',
                            '& .MuiChip-label': {
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            display: 'block',
                            },
                        }}
                    />
                ))}
            </Stack>
        );
    };

    const renderCellContent = (column, row) => {
        const value = row[column.id];
        
        if (column.format) {
            return column.format(row);
        }
        
        switch (column.id) {
            case 'avgcompletion':
                return formatPercentage(value);
            case 'mostSkippedModule':
                return formatSkippedModules(value);
            case 'coursename':
                return (
                    <Typography 
                        variant="body2"                        
                    >
                        {value || '-'}
                    </Typography>
                );
            default:
                return value || '-';
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" gutterBottom className='page-title'>
                Course-Level Engagement Report
            </Typography>
            <Typography variant="body2" sx={{ mb: 4 }}> </Typography>
            
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <TextField
                    placeholder="Courses"
                    className="pink-filter"
                    value={filters.search}
                    onChange={handleSearchChange}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="end">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    }}
                />
                
                <Button
                    variant="outlined"
                    startIcon={<FileDownloadIcon />}
                    onClick={handleExport}
                    disabled={loading}
                >
                    Export CSV
                </Button>
            </Box>

            <Paper sx={{ width: '100%', overflow: 'hidden' }}>
                <ResponsiveTable
                    columns={columns}
                    rows={data}
                    loading={loading}
                    emptyTitle="No courses found"
                    renderCell={renderCellContent}
                    tableProps={{ stickyHeader: true, 'aria-label': 'course engagement table' }}
                    />
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25, 100]}
                    component="div"
                    count={total}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Paper>
        </Box>
    );
};

export default CourseEngagementReport;
